import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LegalLayout from './LegalLayout';
import { usePrerenderSignal } from '../../utils/usePrerenderSignal';
import './LegalPage.css';

/**
 * Consumer Health Data Privacy Policy.
 *
 * Required by Washington's My Health My Data Act (RCW 19.373.020) to be
 * a separate and distinct policy linked from the homepage. Mirrors the
 * disclosures in the main Privacy Policy but scoped to "consumer health
 * data" as defined by the statute (treatment, use of medication, etc.).
 *
 * Nevada residents are also covered by the in-policy summary because
 * Nevada's Consumer Health Data Privacy Law (NRS 603A.400-.420) imposes
 * substantively similar disclosure requirements.
 */
const ConsumerHealthDataPage: React.FC = () => {
  usePrerenderSignal();
  return (
  <>
    <Helmet>
      <title>Consumer Health Data Privacy Policy - PRTCL</title>
      <meta name="description" content="Required by Washington's My Health My Data Act. Disclosures about how PRTCL handles consumer health data, scoped to the statute." />
      <link rel="canonical" href="https://www.prtclapp.com/consumer-health-data" />
      <meta property="og:title" content="Consumer Health Data Privacy Policy - PRTCL" />
      <meta property="og:url" content="https://www.prtclapp.com/consumer-health-data" />
      <meta name="robots" content="noindex,follow" />
    </Helmet>
    <LegalLayout
      title="Consumer Health Data Privacy Policy"
      eyebrow="Privacy"
      updated="Last updated: May 3, 2026"
    >
    <div className="legal-content">
      <h2>1. Purpose of This Policy</h2>
      <p>This Consumer Health Data Privacy Policy describes how PRTCL (operated by Align to Grind LLC) collects, uses, shares, and protects consumer health data, and the rights you have under the Washington My Health My Data Act (RCW 19.373) and Nevada's Consumer Health Data Privacy Law (NRS 603A.400-.420).</p>
      <p>This policy is separate and distinct from our general <Link to="/privacy">Privacy Policy</Link>. To the extent there is any conflict between this policy and the general Privacy Policy with respect to consumer health data, this policy controls for Washington and Nevada residents.</p>

      <h2>2. Categories of Consumer Health Data We Collect</h2>
      <p>PRTCL collects only the categories of consumer health data that you actively enter into the App. These include:</p>
      <ul>
        <li><strong>Treatment information:</strong> Peptide names, dosing schedules, and protocol configurations you choose to set up.</li>
        <li><strong>Use of medication:</strong> Dose log entries (substance, amount, time, notes), including ad-hoc dose entries.</li>
        <li><strong>Bodily functions and health conditions:</strong> Optional notes you may add to dose entries or your profile (such as how a dose felt or side effects you choose to record).</li>
        <li><strong>Health-related supply information:</strong> Vial inventory, reconstitution data, and supply expiration dates.</li>
        <li><strong>Health-related location data:</strong> Injection site selections you log within the App's body map. This is anatomical position information, not GPS or geolocation data.</li>
        <li><strong>Wellness goals:</strong> Goals and experience-level information you optionally provide during onboarding.</li>
      </ul>
      <p>PRTCL does not collect biometric data, genetic data, GPS or precise geolocation data, gender-affirming care data, reproductive or sexual health data, or any consumer health data inferred from outside sources. We collect only what you choose to enter.</p>

      <h2>3. Categories of Sources</h2>
      <p>All consumer health data is collected directly from you through the App's interface. We do not receive consumer health data from third-party data brokers, advertising networks, public records, or any other external source.</p>

      <h2>4. Purposes of Collection and Use</h2>
      <p>We collect and use consumer health data only to provide the features you actively request through the App:</p>
      <ul>
        <li>To store and display your dose logs, protocols, and inventory</li>
        <li>To deliver the dose reminders you configure</li>
        <li>To populate the injection site map you choose to use</li>
        <li>To synchronize your data across your devices</li>
        <li>To respond to support requests you initiate</li>
      </ul>
      <p>Your collection of consumer health data is anchored in the statutory exception for data that is "necessary to provide a product or service that the consumer has requested" (RCW 19.373.030(1)(b)). We do not collect, use, or process consumer health data for any purpose beyond providing the features you request.</p>

      <h2>5. Categories of Third Parties That Receive Consumer Health Data</h2>
      <p>We do not sell consumer health data. We share the minimum necessary consumer health data with the following service providers, each of which is contractually restricted to using your data only to provide the listed service to PRTCL:</p>
      <ul>
        <li><strong>Supabase</strong> - database hosting. Stores your dose logs, protocol data, inventory, and injection site selections.</li>
        <li><strong>Vercel</strong> - web hosting. May process consumer health data in transit when you access the App via the web.</li>
        <li><strong>Apple</strong> - iOS App Store and push notification delivery. Apple does not receive the contents of your dose logs or other consumer health data, but does process the technical metadata required to deliver scheduled reminders to your device.</li>
      </ul>
      <p>We do not share consumer health data with the following:</p>
      <ul>
        <li>Advertising networks or marketing platforms</li>
        <li>Data brokers</li>
        <li>Sentry, RevenueCat, Stripe, Google, Anthropic, or Supadata.ai (these services support other features of the App but do not receive consumer health data)</li>
      </ul>

      <h2>6. We Do Not Sell Consumer Health Data</h2>
      <p>PRTCL does not sell consumer health data, has not sold consumer health data in the past, and has no intention of selling consumer health data in the future. We will not enter into any "valid authorization" arrangement to sell your consumer health data.</p>

      <h2>7. Your Rights</h2>

      <h3>Washington Residents</h3>
      <p>Under the Washington My Health My Data Act, you have the right to:</p>
      <ul>
        <li><strong>Confirm</strong> whether PRTCL is collecting, sharing, or selling your consumer health data, and to access that data</li>
        <li><strong>Withdraw consent</strong> from PRTCL's collection and sharing of your consumer health data</li>
        <li><strong>Have your consumer health data deleted</strong>, including from PRTCL's records and from any affiliates or processors with whom PRTCL has shared it</li>
        <li><strong>Appeal</strong> a denial of any of the above requests</li>
      </ul>

      <h3>Nevada Residents</h3>
      <p>Under Nevada's Consumer Health Data Privacy Law, you have the right to:</p>
      <ul>
        <li>Receive confirmation of whether PRTCL is collecting, sharing, or selling your consumer health data</li>
        <li>Receive a list of third parties with whom we have shared your consumer health data</li>
        <li>Request that we stop collecting, sharing, or selling your consumer health data</li>
        <li>Request deletion of your consumer health data</li>
      </ul>

      <h2>8. How to Exercise Your Rights</h2>
      <p>Most data can be reviewed or deleted directly in the App: open Profile to view, edit, or delete your dose logs, protocols, inventory, and injection site history. To delete your account and all associated consumer health data, use the in-app account deletion option or email us.</p>
      <p>To exercise any of the rights above, including a request for a list of third parties or to withdraw consent, contact:</p>
      <p><strong>Email:</strong> <a href="mailto:support@prtclapp.com">support@prtclapp.com</a><br /><strong>Subject line:</strong> "Consumer Health Data Request"</p>
      <p>We will verify your identity before responding and will respond within 45 days. If we deny your request, you may appeal by replying to our response. If you are not satisfied with the outcome of your appeal, you may file a complaint with the Washington Attorney General (<a href="https://www.atg.wa.gov/file-complaint" target="_blank" rel="noopener noreferrer">atg.wa.gov/file-complaint</a>) or the Nevada Attorney General (<a href="https://ag.nv.gov" target="_blank" rel="noopener noreferrer">ag.nv.gov</a>), as applicable.</p>

      <h2>9. Data Security</h2>
      <p>Consumer health data is encrypted in transit using TLS/SSL and at rest where supported by our infrastructure providers. Access to consumer health data is restricted to your account through Supabase Row-Level Security policies. PRTCL personnel access your consumer health data only when required for support, debugging, or legal compliance, and only with your knowledge or under appropriate confidentiality controls.</p>

      <h2>10. Data Retention</h2>
      <p>Consumer health data is retained for as long as your account is active. Upon account deletion, consumer health data is permanently removed within 30 days, except where retention is required by law.</p>

      <h2>11. Changes to This Policy</h2>
      <p>If we materially change this Consumer Health Data Privacy Policy, we will notify you in the App or by email and update the "Last updated" date above.</p>

      <div className="legal-contact">
        <h2>12. Contact</h2>
        <p>For questions about this Consumer Health Data Privacy Policy:</p>
        <p>Align to Grind LLC<br />Email: <a href="mailto:support@prtclapp.com">support@prtclapp.com</a></p>
      </div>
    </div>
    </LegalLayout>
  </>
  );
};

export default ConsumerHealthDataPage;
